// Simulation send history page. Chronological log of SEND_LOG events with
// multi-select sim / user / dept / location / device / status filters.
// Scope (metadata filters from the shell, incl. sim + user chips from the
// Simulations page) applies before any local filters, so drilled-in scopes
// arrive pre-applied; the status filter can additionally be seeded by the
// drill context (e.g. the Report Rate KPI lands here on Reported).
//
// Calculations: filter SEND_LOG by scope + filters, sort by sent_at DESC.
// The raw event pull is capped server-side at the most-recent 20k rows.
//
// Postgres mapping:
//   `simulations_sent` joined to `users`. Pagination via (sortBy,
//   direction, limit, offset). Status enum must include 'attachment'
//   (referenced by stage-depth scoring).
//
// Integration hook: `useSendHistoryQuery({ startDate, endDate, userEmail,
// status, simName, device, source, limit, offset })` cache 10 min.
import React, { useState, useMemo } from 'react';
import { Box, Button } from '@mui/material';
import { FilterChip } from './MetaNav';
import { motion } from 'framer-motion';
import { SEND_LOG } from '../data';
import ActiveFilters from './ActiveFilters';
import DataTable from './DataTable';

const isIn = (val, vs) => { if (!vs) return true; const a = Array.isArray(val) ? val : [val]; return Array.isArray(vs) ? a.some((x) => vs.includes(x)) : a.includes(vs); };

function applyScopeToSendLog(log, scope) {
    const s = scope || {};
    return log.filter((e) => {
        if (!isIn(e.simName,  s.sim))        return false;
        if (!isIn(e.dept,     s.department)) return false;
        if (!isIn(e.location, s.location))   return false;
        if (!isIn(e.city,     s.city))       return false;
        if (!isIn(e.country,  s.country))    return false;
        if (!isIn(e.company,  s.company))    return false;
        if (!isIn(e.state,    s.state))      return false;
        if (!isIn(e.adGroups,  s.adGroup))    return false;
        if (s.user) {
            const userVals = Array.isArray(s.user) ? s.user : [s.user];
            const full = `${e.firstName} ${e.lastName}`;
            if (!userVals.some((v) => e.email === v || full === v)) return false;
        }
        return true;
    });
}

const fadeUp = { hidden: { opacity: 0, y: 12 }, visible: { opacity: 1, y: 0, transition: { duration: 0.3, ease: 'easeOut' } } };
const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.07 } } };

export const STATUS_META = {
    clicked:     { label: 'Clicked',           bg: 'var(--amber-bg)', text: 'var(--amber-text)', border: 'rgba(255,202,40,.3)',   dot: '#FFCA28' },
    clicked_2nd: { label: '2nd Stage Clicked', bg: 'var(--red-bg)',   text: 'var(--red-text)',   border: 'rgba(255,82,82,.25)',   dot: '#FF5252' },
    reported:    { label: 'Reported',          bg: 'var(--green-bg)', text: 'var(--green-text)', border: 'rgba(102,187,106,.25)', dot: '#66BB6A' },
    no_click:    { label: 'No Engagement',     bg: 'action.hover',    text: 'text.secondary',    border: 'divider',               dot: 'text.disabled' },
};
// Dropdown order mirrors the table's badge vocabulary exactly.
export const STATUS_OPTIONS = ['clicked', 'clicked_2nd', 'reported', 'no_click'];

// API timestamps are UTC without a zone suffix; render them in the viewer's
// local time zone, with the time of day.
export function fmtLocal(iso) {
    if (!iso) return '';
    const s = String(iso);
    const ms = Date.parse(/Z$|[+-]\d\d:?\d\d$/.test(s) ? s : s + 'Z');
    if (Number.isNaN(ms)) return '';
    return new Date(ms).toLocaleString(undefined, { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

// Matches the active-users status chip in UsersPage: 4px-radius chip with a
// leading colored dot rather than the old pill, so status reads consistently
// across the roster and the send log.
export function StatusBadge({ status }) {
    const m = STATUS_META[status] || STATUS_META.no_click;
    return (
        <Box sx={{
            display: 'inline-flex', alignItems: 'center', gap: 0.5,
            px: '6px', py: '1px', borderRadius: '4px',
            fontSize: 10, fontWeight: 600,
            bgcolor: m.bg, color: m.text,
            border: '1px solid', borderColor: m.border,
            whiteSpace: 'nowrap',
        }}>
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: m.dot, flexShrink: 0 }} />
            {m.label}
        </Box>
    );
}

// Multi-select filter: empty selection means 'All'.
// Thin adapter over the shared FilterChip so the send-history filter boxes
// are the exact same control as the Department/Location dimension chips.
// Keeps the original FilterMulti API (array state in, array state out).
export function FilterMulti({ value, onChange, options, placeholder, getLabel }) {
    const toLabel = (o) => (getLabel ? getLabel(o) : String(o));
    const opts = (options || []).map((o) => ({ label: toLabel(o), value: toLabel(o), raw: o }));
    const byValue = new Map(opts.map((o) => [o.value, o.raw]));
    return (
        <FilterChip
            label={placeholder}
            options={opts}
            activeValues={(value || []).map(toLabel)}
            onApply={({ values }) => onChange(values.map((v) => byValue.get(v)).filter((v) => v !== undefined))}
        />
    );
}

// The canonical send-log column set, shared with the Sim Detail recipients
// table so the two stay aligned. includeSim drops the Simulation column when
// every row is already scoped to one sim.
export function sendLogColumns({ includeSim = true } = {}) {
    return [
        { id: 'name',       label: 'Name',              always: true,    sortable: true, type: 'string', accessor: (e) => `${e.firstName} ${e.lastName}` },
        { id: 'email',      label: 'Email',             defaultOn: true, sortable: true, type: 'string', accessor: (e) => e.email },
        ...(includeSim ? [{ id: 'simName', label: 'Simulation', defaultOn: true, sortable: true, type: 'string', accessor: (e) => e.simName }] : []),
        { id: 'status',     label: 'Status',            defaultOn: true, sortable: true, type: 'string', accessor: (e) => e.status,
          render: (e) => <StatusBadge status={e.status} /> },
        // Event timestamps sort on the raw ISO stamp and render in local time.
        { id: 'sent',       label: 'Sent',              defaultOn: true, sortable: true, type: 'string', accessor: (e) => e.sentAt || '',     render: (e) => fmtLocal(e.sentAt) },
        { id: 'clicked',    label: 'Clicked',           defaultOn: true, sortable: true, type: 'string', accessor: (e) => e.clickedAt || '',  render: (e) => fmtLocal(e.clickedAt) },
        { id: 'landed',     label: '2nd Stage Clicked', defaultOn: true, sortable: true, type: 'string', accessor: (e) => e.landedAt || '',   render: (e) => fmtLocal(e.landedAt) },
        { id: 'reported',   label: 'Reported',          defaultOn: true, sortable: true, type: 'string', accessor: (e) => e.reportedAt || '', render: (e) => fmtLocal(e.reportedAt) },
        // Metadata: available via the column picker, off by default.
        { id: 'dept',       label: 'Department', sortable: true, type: 'string', accessor: (e) => e.dept },
        { id: 'location',   label: 'Location',   sortable: true, type: 'string', accessor: (e) => e.location },
        { id: 'city',       label: 'City',       sortable: true, type: 'string', accessor: (e) => e.city },
        { id: 'country',    label: 'Country',    sortable: true, type: 'string', accessor: (e) => e.country },
        { id: 'state',      label: 'State',      sortable: true, type: 'string', accessor: (e) => e.state },
        { id: 'company',    label: 'Company',    sortable: true, type: 'string', accessor: (e) => e.company },
        { id: 'adGroup',    label: 'Group',      sortable: true, type: 'string', accessor: (e) => (Array.isArray(e.adGroups) ? e.adGroups.join(', ') : e.adGroup) },
        { id: 'device',     label: 'Device',     sortable: true, type: 'string', accessor: (e) => e.device || '' },
    ];
}

export default function SendHistoryPage({ drill, scope, removeScope, initialStatus }) {
    const [filterSims, setFilterSims] = useState([]);
    const [filterUsers, setFilterUsers] = useState([]);
    const [filterDepts, setFilterDepts] = useState([]);
    // Seeded by drill context (e.g. the Report Rate KPI lands here with
    // status=reported); fully editable after landing.
    const [filterStatuses, setFilterStatuses] = useState(initialStatus ? [initialStatus] : []);
    const [filterLocations, setFilterLocations] = useState([]);
    const [filterDevices, setFilterDevices] = useState([]);

    const scopedLog = useMemo(() => applyScopeToSendLog(SEND_LOG, scope), [scope]);

    const sims      = useMemo(() => [...new Set(scopedLog.map((e) => e.simName))].sort(), [scopedLog]);
    const users     = useMemo(() => {
        const m = new Map();
        for (const e of scopedLog) if (!m.has(e.email)) m.set(e.email, `${e.firstName} ${e.lastName}`.trim() || e.email);
        return [...m.entries()].map(([email, name]) => ({ email, name })).sort((a, b) => a.name.localeCompare(b.name));
    }, [scopedLog]);
    const depts     = useMemo(() => [...new Set(scopedLog.map((e) => e.dept))].sort(), [scopedLog]);
    const locations = useMemo(() => [...new Set(scopedLog.map((e) => e.location))].sort(), [scopedLog]);
    const devices   = useMemo(() => [...new Set(scopedLog.map((e) => e.device).filter(Boolean))].sort(), [scopedLog]);

    const rows = useMemo(() => scopedLog.filter((e) => {
        if (filterSims.length      && !filterSims.includes(e.simName))                   return false;
        if (filterUsers.length     && !filterUsers.some((u) => u.email === e.email))     return false;
        if (filterDepts.length     && !filterDepts.includes(e.dept))                     return false;
        if (filterStatuses.length  && !filterStatuses.includes(e.status))                return false;
        if (filterLocations.length && !filterLocations.includes(e.location))             return false;
        if (filterDevices.length   && !filterDevices.includes(e.device))                 return false;
        return true;
    }), [scopedLog, filterSims, filterUsers, filterDepts, filterStatuses, filterLocations, filterDevices]);

    const hasFilters = filterSims.length || filterUsers.length || filterDepts.length || filterStatuses.length || filterLocations.length || filterDevices.length;

    const columns = sendLogColumns();

    return (
        <motion.div variants={stagger} initial="hidden" animate="visible" style={{ padding: '0 24px 48px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <ActiveFilters scope={scope} removeScope={removeScope} noPadX />
            <motion.div variants={fadeUp}>
                <DataTable
                    title="Simulation Send History"
                    columns={columns}
                    rows={rows}
                    rowKey={(e) => `${e.email}-${e.simName}-${e.sentAt}`}
                    onRowClick={(e) => drill('user-detail', `${e.firstName} ${e.lastName}`, { type: 'user', value: e.email })}
                    initialSort={{ by: 'sent', dir: 'desc' }}
                    storageKey="rbx-send-history-cols-v2"
                    emptyMessage="No matching delivery events"
                    toolbar={
                        <>
                            <FilterMulti value={filterSims}      onChange={setFilterSims}      options={sims}      placeholder="All simulations" />
                            <FilterMulti value={filterUsers}     onChange={setFilterUsers}     options={users}     placeholder="All users" getLabel={(u) => `${u.name} (${u.email})`} />
                            <FilterMulti value={filterDepts}     onChange={setFilterDepts}     options={depts}     placeholder="All departments" />
                            <FilterMulti value={filterLocations} onChange={setFilterLocations} options={locations} placeholder="All locations" />
                            <FilterMulti value={filterDevices}   onChange={setFilterDevices}   options={devices}   placeholder="All devices" />
                            <FilterMulti value={filterStatuses}  onChange={setFilterStatuses}  options={STATUS_OPTIONS} placeholder="All statuses" getLabel={(s) => STATUS_META[s]?.label || s} />
                            {hasFilters ? (
                                <Button size="small" onClick={() => { setFilterSims([]); setFilterUsers([]); setFilterDepts([]); setFilterStatuses([]); setFilterLocations([]); setFilterDevices([]); }}
                                    sx={{ fontSize: 10, color: 'text.disabled', textTransform: 'none', minWidth: 0, px: 1, '&:hover': { color: 'text.secondary' } }}>
                                    Clear
                                </Button>
                            ) : null}
                        </>
                    }
                />
            </motion.div>
        </motion.div>
    );
}
